/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import defaultTheme from 'tailwindcss/defaultTheme';
import type { AppConfig } from '@/types/config';
import { createLogger } from '@/lib/logger';

const logger = createLogger();

export type DynamicImageDimensions = Array<number | string> | Record<string, number | string>;

// Pre-compiled regex patterns for better performance (compiled once at module load)
/** Matches DIS path and captures the realm: /dw/image/v2/REALM_ID/... */
const DIS_PATH_REALM_REGEX = /\/dw\/image\/v\d+\/([^/]+)/i;
/** Matches DIS path prefix and captures the remaining path */
const DIS_PATH_STRIP_REGEX = /\/dw\/image\/v\d+\/[^/]+(\/.*)/i;
/** Matches DynamicImage placeholder syntax: [?sw={width}], [_{width}], etc. */
const PLACEHOLDER_REGEX = /\[[^\]]*]/g;
/** Matches file extension at end of path: .jpg, .png, etc. */
const FILE_EXTENSION_REGEX = /\.([^.]+)$/;
/** Tests if URL contains DIS path structure */
const IS_DIS_URL_REGEX = /\/dw\/image\/v\d+\//i;
/** Matches dashes for realm conversion (zzrf-001 -> ZZRF_001) */
const DASH_REGEX = /-/g;
/** Tests if URL contains sfrm query parameter (must be preceded by ? or &) */
const HAS_SFRM_PARAM_REGEX = /[?&]sfrm=/;
/** Tests if URL contains quality (q) query parameter (must be preceded by ? or &) */
const HAS_QUALITY_PARAM_REGEX = /[?&]q=/;

export type Image = {
    disBaseLink?: string;
    link?: string;
    [key: string]: unknown;
};

export type DisImageOptions = {
    disHost?: string;
    format?: 'avif' | 'gif' | 'jp2' | 'jpg' | 'jpeg' | 'jxr' | 'png' | 'webp';
    width?: number;
    height?: number;
    quality?: number;
    sourceFormat?: string;
};

export type ImageUrlParams = {
    /** The source image URL to transform */
    src?: string | undefined;
    /** Optional DIS transformation options (format, width, quality) */
    options?: DisImageOptions;
    /** App config containing DIS host and quality settings */
    config?: AppConfig;
    /** Image object */
    image?: Image;
};

function getRealmFromUrl(url: URL): string | undefined {
    // Only extract realm from SFCC Commerce Cloud URLs
    // Example host: zzrf-001.dx.commercecloud.salesforce.com -> realm ZZRF_001
    const isSfccHost =
        url.hostname.endsWith('.commercecloud.salesforce.com') || url.hostname.endsWith('.demandware.net');
    if (!isSfccHost) {
        return undefined;
    }

    // DIS CDN hosts like edge.disstg... do not contain a realm in their subdomain/hostname
    if (url.hostname.startsWith('edge.')) {
        return undefined;
    }

    const parts = url.hostname.split('.');
    let subdomain = parts[0];

    // For Managed Runtime (MRT) domains like app-target.realm.my.commercecloud.salesforce.com
    // parts[2] is 'my' and parts[1] is the realm (e.g. 'zzqi-005')
    if (parts[2] === 'my' && parts[1]) {
        subdomain = parts[1];
    }

    if (!subdomain) {
        return undefined;
    }
    const realm = subdomain.replace(DASH_REGEX, '_').toUpperCase();
    return realm || undefined;
}

/**
 * Normalizes SEO / rewritten library image URLs to standard static asset paths.
 * E.g., /us/en-US/Library_Sites-RefArchSharedLibrary/en_US/dwab5cfd6a/flag.png
 * becomes /on/demandware.static/-/Library-Sites-RefArchSharedLibrary/en_US/dwab5cfd6a/flag.png
 */
export function normalizeImageSrc(src: string): string {
    if (!src) return src;

    // Check for rewritten SEO library URL: /Library_Sites-RefArchSharedLibrary/en_US/dwab5cfd6a/flag.jpg
    const libraryRewriteRegex = /\/Library_Sites-([^/]+)\/([^/]+)\/([^/]+)\/([^?#\s]+)/i;
    const match = src.match(libraryRewriteRegex);
    if (match) {
        const libraryName = match[1];
        const folder = match[2];
        const hash = match[3];
        const filePath = match[4];
        
        // Reconstruct standard path: /on/demandware.static/-/Library-Sites-<LibraryName>/<Folder>/<Hash>/<File>
        const standardPath = `/on/demandware.static/-/Library-Sites-${libraryName}/${folder}/${hash}/${filePath}`;
        
        // Replace the matched portion in the original src string, and strip anything before /on/demandware.static/
        const replaced = src.replace(match[0], standardPath);
        const staticIndex = replaced.indexOf('/on/demandware.static/');
        if (staticIndex !== -1) {
            return replaced.substring(staticIndex);
        }
        return replaced;
    }
    return src;
}

function getRealm(url: URL, config?: AppConfig): string | undefined {
    const realmFromPath = url.pathname.match(DIS_PATH_REALM_REGEX)?.[1];
    if (realmFromPath) {
        return realmFromPath.toUpperCase();
    }
    const realmFromUrl = getRealmFromUrl(url);
    if (realmFromUrl) {
        return realmFromUrl;
    }
    const orgId = config?.commerce?.api?.organizationId;
    if (orgId) {
        const cleanOrgId = orgId.replace(/^f_ecom_/i, '');
        if (cleanOrgId) {
            return cleanOrgId.replace(DASH_REGEX, '_').toUpperCase();
        }
    }
    return undefined;
}

function stripDisPath(pathname: string): string {
    const disPathMatch = pathname.match(DIS_PATH_STRIP_REGEX);
    return disPathMatch?.[1] || pathname;
}

/**
 * Utility helper to convert B2C Commerce static asset URLs into Dynamic Imaging Service (DIS) URLs.
 *
 * When `config.images.enableDis` is false, skips DIS transformation and returns
 * relative static paths for environments that serve images directly.
 *
 * Example:
 * https://zzrf-001.dx.commercecloud.salesforce.com/on/demandware.static/-/Sites-storefront-catalog-m-non-en/default/dwa6379acf/images/slot/landing/cat-landing-slotbanner-mens.jpg
 * becomes
 * https://edge.disstg.commercecloud.salesforce.com/dw/image/v2/ZZRF_001/on/demandware.static/-/Sites-storefront-catalog-m-non-en/default/dwa6379acf/images/slot/landing/cat-landing-slotbanner-mens.webp?sfrm=jpg
 *
 * @see {@link https://help.salesforce.com/s/articleView?id=cc.b2c_image_transformation_service.htm&type=5}
 */
export function toDisImageUrl({ src, options = {}, config }: ImageUrlParams): string | undefined {
    if (!src) {
        return undefined;
    }

    try {
        const normalizedSrc = normalizeImageSrc(src);
        // Extract and preserve any DynamicImage placeholder syntax (e.g., [?sw={width}])
        // These will be appended to the final URL for DynamicImage to process
        const placeholders = normalizedSrc.match(PLACEHOLDER_REGEX) || [];
        const cleanUrl = normalizedSrc.replace(PLACEHOLDER_REGEX, '');

        let url: URL;
        const isAbsolute = cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://');
        if (isAbsolute) {
            url = new URL(cleanUrl);
            const isSfcc =
                url.hostname.endsWith('.commercecloud.salesforce.com') ||
                url.hostname.endsWith('.demandware.net') ||
                IS_DIS_URL_REGEX.test(cleanUrl);
            if (!isSfcc) {
                // External absolute URL, do not transform
                return undefined;
            }
        } else {
            // Relative URL.
            // We only transform relative URLs if they are B2C Commerce static assets
            const isStaticUrl = cleanUrl.includes('/on/demandware.static/');
            if (!isStaticUrl) {
                return undefined;
            }
            const baseHost = config?.images?.host || 'https://localhost';
            const normalizedRelativeUrl = cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`;
            url = new URL(normalizedRelativeUrl, baseHost);
        }

        // When DIS is disabled, skip transformation and use direct static paths
        // (/on/demandware.static paths proxied through Vite dev server config)
        if (config?.images?.enableDis === false) {
            const normalizedPathname = stripDisPath(url.pathname);
            return placeholders.length > 0 ? `${normalizedPathname}${placeholders.join('')}` : normalizedPathname;
        }

        const disHost = options.disHost || config?.images?.host;
        if (!disHost) {
            return undefined;
        }
        const realm = getRealm(url, config);
        if (!realm) {
            return undefined;
        }

        // Remove any existing DIS prefix so we don't duplicate /dw/image/v2/{realm}
        const normalizedPathname = stripDisPath(url.pathname);

        // Derive formats
        const extMatch = normalizedPathname.match(FILE_EXTENSION_REGEX);
        const sourceFormat = options.sourceFormat || extMatch?.[1]?.toLowerCase() || 'jpg';
        const targetFormat = options.format || config?.images?.formats?.[0] || config?.images?.fallbackFormat || 'webp';

        // Replace the extension with target format
        const disPath = normalizedPathname.replace(FILE_EXTENSION_REGEX, `.${targetFormat}`);

        // Build query params
        const search = new URLSearchParams(url.search);
        search.set('sfrm', sourceFormat);
        if (options.width) {
            search.set('sw', String(options.width));
        }
        if (options.height) {
            search.set('sh', String(options.height));
        }
        const quality = options.quality ?? config?.images?.quality;
        search.set('q', String(quality));

        const query = search.toString();
        const baseUrl = `${disHost}/dw/image/v2/${realm}${disPath}${query ? `?${query}` : ''}`;

        // Append preserved placeholders for DynamicImage to process
        return placeholders.length > 0 ? `${baseUrl}${placeholders.join('')}` : baseUrl;
    } catch {
        return undefined;
    }
}

/**
 * Converts an image URL to an optimized format, with graceful fallback.
 *
 * Unlike `toDisImageUrl` which returns `undefined` for non-SFCC URLs,
 * this function returns the original URL as a fallback, making it safer
 * for use with images that may or may not be from SFCC.
 *
 * @param params - Object containing src, options, and config
 * @param params.src - The image URL to transform
 * @param params.options - Optional DIS transformation options (format, width, quality)
 * @param params.config - App config containing DIS host and quality settings
 * @returns Transformed DIS URL, or original URL if transformation not possible
 *
 * @example
 * // SFCC URL - transforms to DIS WebP
 * toImageUrl({ src: 'https://zzrf-001.dx.commercecloud.salesforce.com/.../image.jpg', config })
 * // → 'https://edge.disstg.commercecloud.salesforce.com/dw/image/v2/ZZRF_001/.../image.webp?sfrm=jpg&q=70'
 *
 * // Already DIS URL - ensures WebP format
 * toImageUrl({ src: 'https://edge.disstg.commercecloud.salesforce.com/dw/image/v2/.../image.jpg', config })
 * // → 'https://edge.disstg.commercecloud.salesforce.com/dw/image/v2/.../image.webp?sfrm=jpg'
 *
 * // Non-SFCC URL - returns original (fallback)
 * toImageUrl({ src: 'https://example.com/image.jpg', config })
 * // → 'https://example.com/image.jpg'
 */
export function toImageUrl({ src, options = {}, config, image }: ImageUrlParams): string | undefined {
    const imageUrl = src || image?.disBaseLink || image?.link;

    if (!imageUrl) {
        return undefined;
    }

    try {
        const normalizedSrc = normalizeImageSrc(imageUrl);
        // Extract and preserve any DynamicImage placeholder syntax (e.g., [?sw={width}])
        const placeholders = normalizedSrc.match(PLACEHOLDER_REGEX) || [];
        const cleanUrl = normalizedSrc.replace(PLACEHOLDER_REGEX, '');

        // When DIS is disabled, convert all image URLs to relative static paths
        if (config?.images?.enableDis === false) {
            try {
                let url: URL;
                const isAbsolute = cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://');
                if (isAbsolute) {
                    url = new URL(cleanUrl);
                } else {
                    const isStaticUrl = cleanUrl.includes('/on/demandware.static/');
                    if (!isStaticUrl) {
                        return normalizedSrc;
                    }
                    const baseHost = config?.images?.host || 'https://localhost';
                    const normalizedRelativeUrl = cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`;
                    url = new URL(normalizedRelativeUrl, baseHost);
                }
                const normalizedPathname = stripDisPath(url.pathname);
                return placeholders.length > 0 ? `${normalizedPathname}${placeholders.join('')}` : normalizedPathname;
            } catch {
                return normalizedSrc;
            }
        }

        // If already a DIS URL, check if the host needs rewriting
        const isDisUrl = IS_DIS_URL_REGEX.test(cleanUrl);
        if (isDisUrl) {
            const disHost = options.disHost || config?.images?.host;
            const needsHostRewrite = disHost && !cleanUrl.startsWith(disHost);

            if (!needsHostRewrite) {
                const targetFormat =
                    options.format || config?.images?.formats?.[0] || config?.images?.fallbackFormat || 'webp';
                let transformedUrl = replaceImageFormat(cleanUrl, targetFormat);

                // Add quality parameter if not already present
                const quality = options.quality ?? config?.images?.quality;
                if (quality && !HAS_QUALITY_PARAM_REGEX.test(transformedUrl)) {
                    const separator = transformedUrl.includes('?') ? '&' : '?';
                    transformedUrl = `${transformedUrl}${separator}q=${quality}`;
                }

                return placeholders.length > 0 ? `${transformedUrl}${placeholders.join('')}` : transformedUrl;
            }
        }

        // Try to convert to DIS URL (also handles host rewriting for existing DIS URLs)
        const disUrl = toDisImageUrl({ src: normalizedSrc, options, config });
        // If conversion succeeded, return DIS URL; otherwise return original URL as fallback
        return disUrl ?? normalizedSrc;
    } catch {
        // On any error, return the original URL
        return imageUrl;
    }
}

/**
 * Transforms all image URLs in HTML content to use Dynamic Imaging Service (DIS).
 *
 * This function parses HTML content, finds all `<img>` tags, and transforms their
 * `src` attributes to use DIS URLs with WebP format optimization.
 *
 * @param html - HTML string containing image tags
 * @param config - Application configuration for DIS settings
 * @returns HTML string with transformed image URLs
 *
 * @example
 * const html = '<img src="/on/demandware.static/.../image.jpg" alt="Banner">';
 * const transformed = transformHtmlImageUrls(html, config);
 * // Returns: '<img src="https://edge.disstg.../image.webp?sfrm=jpg&q=70" alt="Banner">'
 */
export function transformHtmlImageUrls(html: string, config: AppConfig): string {
    // Return empty string for null/undefined to maintain type safety
    if (!html) {
        return '';
    }

    // Short-circuit if no image tags present (performance optimization)
    if (!html.includes('<img')) return html;

    // Regular expression to match <img> tags with src attributes
    const imgTagRegex = /<img\s+[^>]*src=["']([^"']+)["'][^>]*>/gi;

    return html.replace(imgTagRegex, (match, srcValue: string) => {
        // Transform the src URL using toImageUrl
        const transformedSrc = toImageUrl({ src: srcValue, config });

        // If transformation succeeded, replace only the src attribute value
        if (transformedSrc && transformedSrc !== srcValue) {
            // Use targeted regex to replace only src attribute, preserving quotes
            return match.replace(/src=(["'])([^"']+)\1/i, (_, quote) => `src=${quote}${transformedSrc}${quote}`);
        }

        // If transformation failed or URL unchanged, return original match
        return match;
    });
}

/**
 * Supported target formats of Salesforce's Dynamic Imaging Service are: avif, gif, jp2, jpg, jpeg, jxr, png, and webp.
 * @see {@link https://help.salesforce.com/s/articleView?id=cc.b2c_image_transformation_service.htm&type=5}
 * @see {@link https://help.salesforce.com/s/articleView?id=cc.b2c_creating_image_transformation_urls.htm&type=5}
 */
type DynamicImageFormat = 'avif' | 'gif' | 'jp2' | 'jpg' | 'jpeg' | 'jxr' | 'png' | 'webp';

export const defaultImageFormats: Array<DynamicImageFormat> = ['webp'];

const vwValue = /^[\d.]+vw$/;
const pxValue = /^[\d.]+px$/;
const emValue = /^[\d.]+em$/;
const remValue = /^[\d.]+rem$/;
const imageExtensions = /\.(avif|gif|jp2|jpe?g|png|tiff?|webp)(?=\?|$)/i;

// Tailwind CSS default breakpoints (converted from rem to px)
const defaultBreakpoints = {
    base: '0px',
    ...Object.fromEntries(
        Object.entries(defaultTheme.screens).map(([key, value]) => [
            key,
            remValue.test(value) ? `${parseFloat(value) * 16}px` : value,
        ])
    ),
} as const;

type Breakpoints = typeof defaultBreakpoints;
type BreakpointKey = keyof Breakpoints;

const getBreakpointLabels = (breakpoints: Record<string, string>): string[] =>
    Object.entries(breakpoints)
        .sort((a, b) => parseFloat(a[1]) - parseFloat(b[1]))
        .map(([key]) => key);

let themeBreakpoints = defaultBreakpoints;
let breakpointLabels = getBreakpointLabels(themeBreakpoints);

/**
 * Helper to create very specific `media` attributes for responsive preload purposes.
 * @see {@link https://web.dev/articles/preload-responsive-images#picture}
 */
const obtainImageLinkMedia = (breakpointIndex: number): { min?: string; max?: string } | undefined => {
    const toMediaValue = (bp: string, type: 'min' | 'max') => {
        const val = themeBreakpoints[bp as BreakpointKey];
        if (emValue.test(val)) {
            // em value
            const parsed = parseFloat(val);
            return { [type]: type === 'max' ? `${parsed - 0.01}em` : `${parsed}em` };
        }

        const parsed = parseInt(val, 10);
        return { [type]: type === 'max' ? `${parsed - 1}px` : `${parsed}px` };
    };

    const nextBp = breakpointLabels[breakpointIndex + 1];
    const currentBp = breakpointLabels[breakpointIndex];
    if (breakpointIndex === 0) {
        // first
        return toMediaValue(nextBp, 'max');
    } else if (breakpointIndex === breakpointLabels.length - 1) {
        // last
        return toMediaValue(currentBp, 'min');
    }
    return { ...toMediaValue(currentBp, 'min'), ...toMediaValue(nextBp, 'max') };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isObject = (o: any): o is Record<string, any> => o?.constructor === Object;

/**
 * @example
 * // returns the array [10, 10, 10, 50]
 * widthsAsArray({base: 10, lg: 50})
 */
const widthsAsArray = (widths: Record<string, number | string>): Array<number | string> => {
    const biggestBreakpoint = breakpointLabels.filter((bp) => Boolean(widths[bp])).pop();

    if (!biggestBreakpoint) {
        return [];
    }

    const biggestBreakpointIndex = breakpointLabels.indexOf(biggestBreakpoint);
    let mostRecent: number | string | undefined;
    return breakpointLabels
        .slice(0, biggestBreakpointIndex + 1)
        .map((bp) => {
            if (widths[bp]) {
                mostRecent = widths[bp];
                return widths[bp];
            }
            return mostRecent;
        })
        .filter((item): item is number | string => item !== undefined);
};

const emToPx = (em: number, browserDefaultFontSize = 16): number => Math.round(em * browserDefaultFontSize);

const vwToPx = (vw: number, breakpoint: string): number => {
    const result = (vw / 100) * parseFloat(themeBreakpoints[breakpoint as BreakpointKey]);
    const breakpointsDefinedInPx = Object.values(themeBreakpoints).some((val) => pxValue.test(val));

    // Assumes theme's breakpoints are defined in either em or px
    return breakpointsDefinedInPx ? result : emToPx(result);
};

/**
 * Replaces the image file extension in a URL with a configurable target format, e.g. `webp`.
 * Handles URLs with query parameters correctly.
 * If the format changes, appends the original extension as `sfrm` parameter.
 * @example
 * // returns 'https://example.com/image.webp?sw=460&q=60&sfrm=jpg'
 * replaceImageFormat('https://example.com/image.jpg?sw=460&q=60')
 */
export const replaceImageFormat = (
    url: string,
    targetFormat: 'webp' | 'avif' | 'gif' | 'jp2' | 'jpg' | 'jpeg' | 'jxr' | 'png' = 'webp'
): string => {
    // If URL already has sfrm parameter, it's already been transformed - return as-is
    if (HAS_SFRM_PARAM_REGEX.test(url)) {
        return url;
    }

    const match = url.match(imageExtensions);
    if (!match) {
        return url;
    }

    const originalExtension = match[1].toLowerCase();
    if (originalExtension === targetFormat) {
        return url;
    }

    const newUrl = url.replace(imageExtensions, `.${targetFormat}`);
    const separator = newUrl.includes('?') ? '&' : '?';
    return `${newUrl}${separator}sfrm=${originalExtension}`;
};

/**
 * @example
 * // returns https://example.com/image_720.webp?sw=720&q=60&sfrm=jpg
 * getSrc('https://example.com/image[_{width}].jpg', { w: 720, q: 60 })
 */
export const getSrc = (
    dynamicSrc: string,
    {
        w,
        h,
        q,
        f,
    }: {
        /** Image width in px — maps to DIS `sw` parameter */
        w?: number;
        /** Image height in px — maps to DIS `sh` parameter */
        h?: number;
        /** Image quality (1-100) — maps to DIS `q` parameter */
        q?: number;
        /** Target image format — triggers format conversion via `sfrm` */
        f?: 'webp' | 'avif' | 'gif' | 'jp2' | 'jpg' | 'jpeg' | 'jxr' | 'png';
    } = {}
): string => {
    const getSep = (res: string): '?' | '&' => (res.includes('?') ? '&' : '?');
    const hasUrlParam = (url: string, param: string) => new RegExp(`[?&]${param}=`).test(url);

    // 1. Remove surrounding brackets from placeholder syntax
    // 2. Handle edge cases where DIS instructions are added to an already parameterized URL
    // 3. Replace named placeholders: {width} → w, {height} → h
    // 4. Replace any remaining unnamed placeholders with primary dimension (backward compat)
    const widthStr = w != null ? w.toString() : '';
    const heightStr = h != null ? h.toString() : widthStr;
    const fallbackStr = widthStr || heightStr;
    let result = dynamicSrc
        .replace(/\[([?&]?)([^\]]+)]/g, (_match, _sep, content, offset, fullString) => {
            const beforeMatch = fullString?.slice?.(0, offset);
            return `${getSep(beforeMatch)}${content}`;
        })
        .replace(/\{width}/g, widthStr)
        .replace(/\{height}/g, heightStr)
        .replace(/\{[^}]+}/g, fallbackStr);

    // Handle sw= parameter - only added when width is explicitly provided
    if (w != null) {
        if (hasUrlParam(result, 'sw')) {
            result = result.replace(/([?&])sw=\d+/, `$1sw=${w}`);
        } else {
            result = `${result}${getSep(result)}sw=${w}`;
        }
    }

    // Handle sh= parameter - only added when height is explicitly provided
    if (h != null) {
        if (hasUrlParam(result, 'sh')) {
            result = result.replace(/([?&])sh=\d+/, `$1sh=${h}`);
        } else {
            result = `${result}${getSep(result)}sh=${h}`;
        }
    }

    // Handle quality parameter - existing q= in URL takes priority
    if (typeof q === 'number' && Number.isInteger(q) && !hasUrlParam(result, 'q')) {
        result = `${result}${getSep(result)}q=${q}`;
    }

    // If no target format specified, don't convert format (keep original)
    // This is important for environments where DIS format conversion isn't available
    if (f) {
        return replaceImageFormat(result, f);
    }
    return result;
};

/**
 * @example
 * // Returns 'https://example.com/image.jpg'
 * getSrcWithoutOptionalParams('https://example.com/image.jpg[?sw={width}]')
 */
const getSrcWithoutOptionalParams = (dynamicSrc: string): string => dynamicSrc.replace(/\[[^\]]+]/g, '');

const padArray = (arr: Array<number | string>): Array<number | string> => {
    const l1 = arr.length;
    const l2 = breakpointLabels.length;
    if (l1 < l2) {
        const lastEntry = arr[arr.length - 1];
        const amountToPad = l2 - l1;
        return [...arr, ...Array(amountToPad).fill(lastEntry)];
    }
    return arr;
};

const convertToPxNumbers = (widths: Array<number | string>): Array<number> =>
    widths
        .map((width, i) => {
            if (typeof width === 'number') {
                return width;
            }

            if (vwValue.test(width)) {
                const vw = parseFloat(width);
                const currentBp = breakpointLabels[i];
                // We imagine the biggest image for the current breakpoint
                // to be when the viewport is closely approaching the _next breakpoint_.
                const nextBp = breakpointLabels[i + 1];

                if (nextBp) {
                    return vwToPx(vw, nextBp);
                }
                // We're already at the last breakpoint
                return widths[i] !== widths[i - 1] ? vwToPx(vw, currentBp) : undefined;
            } else if (pxValue.test(width)) {
                return parseInt(width, 10);
            } else {
                logger.error('Expecting to see values with vw or px unit only');
                return 0;
            }
        })
        .filter((width): width is number => width !== undefined);

type ImageLink = {
    type: string;
    srcSet: string;
    sizes: string;
    media: { min?: string; max?: string };
};

type ConvertedImageLink = {
    type: string;
    srcSet: string;
    sizes: string;
    media: string;
};

/**
 * Transforms an array of preload link objects by converting the raw `media`
 * property of each entry (with `min` and/or `max` values) into actual media
 * queries using `(min-width)` and/or `(max-width)`.
 */
const convertImageLinksMedia = (links: ImageLink[]): ConvertedImageLink[] =>
    links.map((link) => {
        const {
            media: { min, max },
        } = link;
        const acc: string[] = [];
        if (min) {
            acc.push(`(min-width: ${min})`);
        }
        if (max) {
            acc.push(`(max-width: ${max})`);
        }
        return { ...link, media: acc.join(' and ') };
    });

type Source = {
    type: string;
    srcSet: string;
    sizes: string;
    media: string;
};

type ResponsiveData = {
    sources: Source[];
    links: ConvertedImageLink[];
};

const toMimeType = (format: DynamicImageFormat) => (format === 'jpg' ? 'image/jpeg' : `image/${format}`);

/**
 * Determines the data required for the responsive `<source>` and `<link rel="preload" type="image/{format}">
 * portions/elements.
 */
const getResponsiveSourcesAndLinks = (
    src: string,
    {
        widths,
        heights,
        formats,
        quality,
    }: {
        widths?: Array<number | string>;
        heights?: Array<number | string>;
        formats: Array<DynamicImageFormat>;
        quality?: number;
    }
): ResponsiveData => {
    // Use widths as the primary sizing dimension; fall back to heights for the `sizes` attribute
    // when only heights are provided (heights-only mode).
    const sizingDimension = widths ?? heights ?? [];

    // By default, unitless value is interpreted as px
    const sizeValues = sizingDimension.map((dim) => (typeof dim === 'number' ? `${dim}px` : dim));
    const l = sizeValues.length;

    const _sizes = breakpointLabels.map((bp, i) => {
        return i === 0
            ? {
                  media: '',
                  mediaLink: obtainImageLinkMedia(i),
                  sizes: sizeValues[i],
              }
            : {
                  media: `(min-width: ${themeBreakpoints[bp as BreakpointKey]})`,
                  mediaLink: obtainImageLinkMedia(i),
                  sizes: sizeValues.at(i >= l ? l - 1 : i),
              };
    });

    const sourcesWidths = widths ? convertToPxNumbers(padArray(widths)) : undefined;
    const sourcesHeights = heights ? convertToPxNumbers(padArray(heights)) : undefined;
    const sourcesLength = sourcesWidths?.length ?? sourcesHeights?.length ?? 0;
    const sourcesHeightsLength = sourcesHeights?.length ?? 0;
    // Use first format for srcSet generation (when DIS is disabled, formats is empty so no conversion occurs)
    const targetFormat = formats[0];
    const { sources, links } = breakpointLabels.reduce(
        (acc: { sources: Source[]; links: ImageLink[] }, _bp, idx) => {
            const width = sourcesWidths
                ? sourcesWidths[idx >= sourcesWidths.length ? sourcesWidths.length - 1 : idx]
                : undefined;
            const height = sourcesHeights
                ? sourcesHeights[idx >= sourcesHeightsLength ? sourcesHeightsLength - 1 : idx]
                : undefined;
            // The descriptor dimension used for the `w` descriptor in srcSet
            const descriptorDimension = width ?? height;
            const sizeData = _sizes[idx];
            if (!sizeData || !descriptorDimension) {
                return acc;
            }

            const { sizes, media, mediaLink } = sizeData;
            const firstSource = acc.sources[0];
            const lastLink = acc.links[acc.links.length - 1];
            const srcSet = [1, 2]
                .map((factor) => {
                    const effectiveWidth = width != null ? Math.round(width * factor) : undefined;
                    const effectiveHeight = height != null ? Math.round(height * factor) : undefined;
                    const descriptorValue = Math.round(descriptorDimension * factor);

                    return `${getSrc(src, { w: effectiveWidth, h: effectiveHeight, q: quality, f: targetFormat })} ${descriptorValue}w`;
                })
                .join(', ');

            if (idx < sourcesLength && sizes && (firstSource?.sizes !== sizes || srcSet !== firstSource?.srcSet)) {
                // Only store new `<source>` if we haven't already stored those values
                // Insert at beginning to achieve reversed `<source>` order
                for (let i = formats.length - 1; i >= 0; i--) {
                    acc.sources.unshift({ type: toMimeType(formats[i]), srcSet, sizes, media });
                }
            }

            if (sizes && (lastLink?.sizes !== sizes || srcSet !== lastLink?.srcSet)) {
                // Only store new `<link>` if we haven't already stored those values
                for (const format of formats) {
                    acc.links.push({ type: toMimeType(format), srcSet, sizes, media: mediaLink || {} });
                }
            } else if (lastLink && mediaLink) {
                // If we have already stored those values, update the `max` portion of the related `<link>` data
                lastLink.media.max = mediaLink.max;
            }
            return acc;
        },
        { sources: [], links: [] }
    );
    return { sources, links: convertImageLinksMedia(links) };
};

/**
 * Resolve the attributes required to create a DIS-optimized `<picture>` component.
 */
export const getResponsivePictureAttributes = ({
    src,
    widths,
    heights,
    formats = defaultImageFormats,
    breakpoints = defaultBreakpoints,
    quality,
}: {
    src: string;
    /**
     * Image widths relative to the breakpoints. Supports multiple formats:
     * - Array of numbers: [100, 360, 720] (unitless, interpreted as px)
     * - Array of strings with units: ['50vw', '100vw', '500px'] (mixed px and vw units)
     * - Object with breakpoint keys: {base: 100, sm: 360, md: 720} (unitless, interpreted as px)
     * - Object with breakpoint keys and units: {base: '100vw', sm: '50vw', md: '500px'}
     */
    widths?: DynamicImageDimensions;
    /**
     * Image heights relative to the breakpoints, used for DIS server-side cropping via
     * the `sh` parameter. Supports the same formats as `widths`. When provided alongside
     * `widths`, defines the exact crop box on the DIS server. When omitted, DIS preserves
     * the original aspect ratio based on `sw` alone.
     */
    heights?: DynamicImageDimensions;
    formats?: Array<DynamicImageFormat>;
    breakpoints?: Record<string, string>;
    /**
     * Image quality (1-100). If the source URL already contains a `q` parameter,
     * that value takes priority over this setting.
     */
    quality?: number;
}): {
    sources: Source[];
    links: ConvertedImageLink[];
    src: string;
} => {
    if (!widths && !heights) {
        return {
            sources: [],
            links: [],
            src: getSrcWithoutOptionalParams(src),
        };
    }

    if (breakpoints !== themeBreakpoints) {
        themeBreakpoints = breakpoints as typeof defaultBreakpoints;
        breakpointLabels = getBreakpointLabels(themeBreakpoints);
    }

    const _widths = widths
        ? isObject(widths)
            ? widthsAsArray(widths as Record<string, number | string>)
            : (widths as Array<number | string>).slice(0)
        : undefined;
    const _heights = heights
        ? isObject(heights)
            ? widthsAsArray(heights as Record<string, number | string>)
            : (heights as Array<number | string>).slice(0)
        : undefined;
    const { sources, links } = getResponsiveSourcesAndLinks(src, {
        widths: _widths,
        heights: _heights,
        formats,
        quality,
    });

    return {
        sources,
        links,
        src: getSrcWithoutOptionalParams(src),
    };
};
